import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { 
  ChartLine, 
  Buildings, 
  ShieldCheck, 
  Lightning,
  ArrowRight,
  CheckCircle,
  Sparkle
} from '@phosphor-icons/react';

// Logo component
const FinanciALLogo = () => (
  <img 
    src="https://customer-assets-0z36b82j.emergentagent.net/job_1ce9899f-22e9-4951-9d4b-a91c40afd391/artifacts/ktwxigwh_Liquid%20Glass%20Logo.png.png" 
    alt="FinanciAL" 
    className="w-10 h-10 rounded-xl object-cover"
  />
);

const fadeIn = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.5 }
};

const staggerContainer = {
  animate: {
    transition: {
      staggerChildren: 0.1
    }
  }
};

const Landing = () => {
  const features = [
    {
      icon: Buildings,
      title: 'Menaxhim Multi-Kompanish',
      description: 'Menaxhoni kompani të pakufizuara nga një panel i vetëm. Secila me të dhëna dhe cilësime të izoluara.'
    },
    {
      icon: ChartLine,
      title: 'Analiza Financiare',
      description: 'Pasqyra të ardhurave, bilance, analiza të rrjedhës së parasë me llogaritje automatike të raporteve.'
    },
    {
      icon: Sparkle,
      title: 'Njohuri të Fuqizuara nga AI',
      description: 'Merrni analiza inteligjente që theksojnë rreziqet, anomalitë dhe mundësitë e rritjes.'
    },
    {
      icon: ShieldCheck,
      title: 'Raporte të Gatshme për Auditim',
      description: 'Gjeneroni raporte në përputhje me gjurmë të plota auditimi dhe kontroll versioni.'
    }
  ];

  const plans = [
    {
      name: 'Bazik',
      price: '29€',
      period: '/muaj',
      features: ['5 Kompani', '50 Dokumente', 'Raporte Standarde', 'Mbështetje Prioritare', 'Eksport të Dhënash'],
      popular: false
    },
    {
      name: 'Profesional',
      price: '79€',
      period: '/muaj',
      features: ['20 Kompani', '500 Dokumente', 'Raporte të Avancuara', 'Njohuri AI', 'Qasje API'],
      popular: true
    },
    {
      name: 'Ndërmarrje',
      price: '199€',
      period: '/muaj',
      features: ['Kompani të Pakufizuara', 'Dokumente të Pakufizuara', 'Të Gjitha Veçoritë', 'Mbështetje Dedikuar', 'SLA'],
      popular: false
    }
  ];

  return (
    <div className="min-h-screen bg-[#F4F4F0] relative grain">
      {/* Navigation */}
      <nav className="fixed top-4 left-1/2 -translate-x-1/2 w-[90%] max-w-5xl glass border border-white/20 rounded-full shadow-2xl z-50 px-6 py-3">
        <div className="flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <FinanciALLogo />
            <span className="font-semibold text-xl text-[#1A3C34]">FinanciAL</span>
          </Link>
          
          <div className="hidden md:flex items-center gap-8">
            <a href="#features" className="text-[#1A3C34]/70 hover:text-[#1A3C34] transition-colors">Veçoritë</a>
            <a href="#pricing" className="text-[#1A3C34]/70 hover:text-[#1A3C34] transition-colors">Çmimet</a>
          </div>
          
          <div className="flex items-center gap-3">
            <Link to="/login">
              <Button variant="ghost" className="text-[#1A3C34]" data-testid="nav-login-btn">
                Hyr
              </Button>
            </Link>
            <Link to="/register">
              <Button className="bg-[#1A3C34] hover:bg-[#1A3C34]/90 text-white rounded-full px-6" data-testid="nav-register-btn">
                Fillo Tani
              </Button>
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6">
        <div className="max-w-7xl mx-auto">
          <motion.div 
            className="text-center max-w-4xl mx-auto"
            initial="initial"
            animate="animate"
            variants={staggerContainer}
          >
            <motion.div variants={fadeIn} className="mb-6">
              <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#D4A373]/20 text-[#1A3C34] text-sm font-medium">
                <Lightning weight="fill" className="w-4 h-4 text-[#D4A373]" />
                Besuar nga 500+ Firma Kontabiliteti
              </span>
            </motion.div>
            
            <motion.h1 
              variants={fadeIn}
              className="font-marketing text-5xl md:text-6xl lg:text-7xl font-light text-[#1A3C34] tracking-tight leading-none mb-6"
            >
              Analiza Financiare
              <br />
              <span className="font-semibold">E Thjeshtuar</span>
            </motion.h1>
            
            <motion.p 
              variants={fadeIn}
              className="text-lg md:text-xl text-[#1A3C34]/70 mb-10 max-w-2xl mx-auto leading-relaxed"
            >
              Platforma e nivelit enterprise për kontabilistë profesionistë. Analizoni, dokumentoni dhe paraqitni rezultatet financiare me njohuri të fuqizuara nga AI.
            </motion.p>
            
            <motion.div variants={fadeIn} className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/register">
                <Button 
                  size="lg" 
                  className="bg-[#1A3C34] hover:bg-[#1A3C34]/90 text-white rounded-full px-8 py-6 text-lg shadow-xl hover:shadow-2xl transition-all"
                  data-testid="hero-get-started-btn"
                >
                  Fillo Provën Falas
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <Button 
                variant="outline" 
                size="lg"
                className="border-[#1A3C34]/20 text-[#1A3C34] rounded-full px-8 py-6 text-lg"
              >
                Shiko Demo
              </Button>
            </motion.div>
          </motion.div>
          
          {/* Hero Image */}
          <motion.div 
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="mt-16 relative"
          >
            <div className="rounded-2xl overflow-hidden shadow-2xl border border-[#1A3C34]/10">
              <img 
                src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=1600&q=80" 
                alt="Parashikim i Panelit"
                className="w-full h-auto"
              />
            </div>
            <div className="absolute -bottom-6 -right-6 w-48 h-48 bg-[#D4A373]/20 rounded-full blur-3xl" />
            <div className="absolute -top-6 -left-6 w-32 h-32 bg-[#1A3C34]/10 rounded-full blur-3xl" />
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-[#D4A373] font-medium tracking-widest text-xs uppercase">Veçoritë</span>
            <h2 className="font-marketing text-4xl md:text-5xl text-[#1A3C34] mt-4 tracking-tight">
              Gjithçka që Ju Nevojitet
            </h2>
            <p className="text-[#1A3C34]/60 mt-4 max-w-xl mx-auto">
              Mjete gjithëpërfshirëse të dizajnuara posaçërisht për kontabilistë profesionistë dhe analistë financiarë.
            </p>
          </motion.div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className="h-full border-[#1A3C34]/10 hover:border-[#D4A373]/50 transition-colors card-hover">
                  <CardContent className="p-6">
                    <div className="w-12 h-12 rounded-xl bg-[#1A3C34]/5 flex items-center justify-center mb-4">
                      <feature.icon className="w-6 h-6 text-[#1A3C34]" weight="duotone" />
                    </div>
                    <h3 className="font-semibold text-[#1A3C34] mb-2">{feature.title}</h3>
                    <p className="text-sm text-[#1A3C34]/60 leading-relaxed">{feature.description}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-20 px-6 bg-[#F4F4F0]">
        <div className="max-w-7xl mx-auto">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-[#D4A373] font-medium tracking-widest text-xs uppercase">Çmimet</span>
            <h2 className="font-marketing text-4xl md:text-5xl text-[#1A3C34] mt-4 tracking-tight">
              Çmime të Thjeshta, Transparente
            </h2>
            <p className="text-[#1A3C34]/60 mt-4 max-w-xl mx-auto">
              Zgjidhni planin që i përshtatet praktikës suaj. Të gjitha planet përfshijnë 14 ditë provë falas.
            </p>
          </motion.div>
          
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {plans.map((plan, index) => (
              <motion.div
                key={plan.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className={`h-full relative ${plan.popular ? 'border-[#1A3C34] border-2 shadow-xl' : 'border-[#1A3C34]/10'}`}>
                  {plan.popular && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 bg-[#1A3C34] text-white text-xs font-medium rounded-full">
                      Më i Popullarizuar
                    </div>
                  )}
                  <CardContent className="p-8">
                    <h3 className="text-xl font-semibold text-[#1A3C34] mb-2">{plan.name}</h3>
                    <div className="flex items-baseline gap-1 mb-6">
                      <span className="font-marketing text-4xl font-semibold text-[#1A3C34]">{plan.price}</span>
                      <span className="text-[#1A3C34]/60">{plan.period}</span>
                    </div>
                    <ul className="space-y-3 mb-8">
                      {plan.features.map((feature) => (
                        <li key={feature} className="flex items-center gap-2 text-sm text-[#1A3C34]/70">
                          <CheckCircle className="w-5 h-5 text-[#2A9D8F]" weight="fill" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                    <Link to="/register">
                      <Button 
                        className={`w-full rounded-full ${
                          plan.popular 
                            ? 'bg-[#1A3C34] hover:bg-[#1A3C34]/90 text-white' 
                            : 'bg-[#1A3C34]/10 hover:bg-[#1A3C34]/20 text-[#1A3C34]'
                        }`}
                        data-testid={`pricing-${plan.name.toLowerCase()}-btn`}
                      >
                        Fillo Tani
                      </Button>
                    </Link>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6 bg-[#1A3C34]">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-marketing text-4xl md:text-5xl text-white tracking-tight mb-6">
              Gati të Transformoni Praktikën Tuaj?
            </h2>
            <p className="text-white/70 text-lg mb-8 max-w-2xl mx-auto">
              Bashkohuni me qindra profesionistë të kontabilitetit që i besojnë FinanciAL për nevojat e tyre të analizës financiare.
            </p>
            <Link to="/register">
              <Button 
                size="lg" 
                className="bg-[#D4A373] hover:bg-[#D4A373]/90 text-[#1A3C34] rounded-full px-8 py-6 text-lg shadow-xl"
                data-testid="cta-get-started-btn"
              >
                Filloni Provën Tuaj Falas
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 bg-[#1A3C34] border-t border-white/10">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex items-center gap-2">
              <img 
                src="https://customer-assets-0z36b82j.emergentagent.net/job_1ce9899f-22e9-4951-9d4b-a91c40afd391/artifacts/ktwxigwh_Liquid%20Glass%20Logo.png.png" 
                alt="FinanciAL" 
                className="w-8 h-8 rounded-lg object-cover"
              />
              <span className="font-semibold text-white">FinanciAL</span>
            </div>
            <p className="text-white/50 text-sm">
              © 2025 FinanciAL. Të gjitha të drejtat e rezervuara.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
